import PopupWithDimmedBackground from 'components/PopupWithDimmedBackground';
import RoundedButton from 'components/RoundedButton';
import Subheading from 'components/Subheading';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import $ from 'jquery';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Form, Icon, Message } from 'semantic-ui-react';
import { playerApiClient } from 'utils/apiClient';
import { ASSET_PREFIX, colors } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import { resizeImageUrl } from 'utils/image/imageResizeService';
import { validLeaderboardUsername } from 'utils/RegistrationValidator';

interface ILeaderboardDetailsChooser {
  onSuccess: () => Promise<void>;
  onClose: () => void;
  isLeaderboardPage?: boolean;
}

const LeaderboardDetailsChooser = observer(
  ({
    onSuccess,
    onClose,
    isLeaderboardPage = false,
  }: ILeaderboardDetailsChooser) => {
    const { currentUser } = useUserStore();
    const { displayToast } = useUIStore();

    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const [usernameEdited, setUsernameEdited] = useState(false);
    const [username, setUsername] = useState(currentUser?.friendlyName ?? '');

    const [doneButtonLoading, setDoneButtonLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const hadPictureBefore = currentUser?.avatarLocation !== null;
    const [clearedExistingPicture, setClearedExistingPicture] = useState(false);
    const [previewImage, setPreviewImage] = useState<string | null>(
      (currentUser?.avatarLocation && resizeImageUrl(currentUser?.avatarLocation, { width: 240, height: 240 })) ?? null
    );

    if (selectedFile) {
      const reader = new FileReader();

      reader.onload = (e) => {
        setPreviewImage(e.target ? (e.target.result as string) : null);
      };

      reader.readAsDataURL(selectedFile);
    }

    return (
      <PopupWithDimmedBackground
        whiteCloseButton={undefined}
        icon={undefined}
        additionalClassName={undefined}
        bannerImage={undefined}
        onClose={undefined}
        margin={'3em 0 0em'}
      >
        <img
          id="closeButton"
          src={`${ASSET_PREFIX}/images/close_button.svg`}
          style={{ cursor: 'pointer', width: '15px' }}
          onClick={onClose}
          alt="close button - cross"
        />
        <Subheading>
          {isLeaderboardPage ? <>Your details</> : <>MY PROFILE</>}
        </Subheading>
        <div
          className="initialsCircle"
          style={{
            marginBottom: 0,
            overflow: 'hidden',
            position: 'relative',
            cursor: 'pointer',
          }}
          onClick={() => $('#fileUploadButton').click()}
        >
          {currentUser?.firstName?.charAt(0) ?? ''}
          {currentUser?.lastName?.charAt(0) ?? ''}

          {previewImage && (
            <img
              src={previewImage}
              style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                objectFit: 'cover',
                left: 0,
                top: 0,
              }}
            />
          )}
        </div>
        {(selectedFile || (previewImage && hadPictureBefore)) && (
          <Icon
            name="trash"
            style={{
              fontSize: '2em',
              height: 'auto',
              cursor: 'pointer',
              marginBottom: '10px',
            }}
            onClick={() => {
              setSelectedFile(null);
              setPreviewImage(null);

              if (hadPictureBefore) {
                setClearedExistingPicture(true);
              }

              const buttonForm = $('#buttonForm')[0];
              // @ts-ignore
              buttonForm.reset();
            }}
          />
        )}
        <img
          className="randomIcon"
          style={{ marginTop: '30px' }}
          src={`${ASSET_PREFIX}/images/account/details/picture-logo.png`}
        />
        <h5
          style={{
            textAlign: 'center',
            margin: 0,
            textTransform: 'none',
            letterSpacing: '0',
            marginTop: 0,
            marginBottom: 10,
          }}
        >
          Change your picture
        </h5>

        <Form
          style={{
            width: '100%',
            height: '45px',
            borderRadius: '60px',
            border: '3px solid #e3e3e3',
            marginBottom: '30px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexDirection: 'row',
            padding: '4px',
          }}
          id="buttonForm"
        >
          <Form.Field
            style={{
              width: '150px',
              borderRadius: '60px',
              height: '100%',
              backgroundColor: '#E1E1E1',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <label
              style={{ fontWeight: '700', margin: 0 }}
              htmlFor="fileUploadButton"
            >
              Choose file
            </label>
            <input
              id="fileUploadButton"
              type="file"
              accept="image/*"
              onChange={(e) => {
                setSelectedFile(e.target.files ? e.target.files[0] : null);
              }}
              style={{
                marginBottom: '2.5em',
                display: 'none',
              }}
              className="neonInput"
            />
          </Form.Field>
        </Form>

        <img
          className="randomIcon"
          src={`${ASSET_PREFIX}/images/account/details/username.png`}
        />
        <h5
          style={{
            textAlign: 'center',
            marginBottom: 4,
            marginTop: 0,
            textTransform: 'none',
            letterSpacing: '0',
          }}
        >
          Change your username
        </h5>

        <Form id="form" style={{ flexShrink: 0 }}>
          <Form.Field
            error={usernameEdited && !validLeaderboardUsername(username)}
          >
            <input
              type="text"
              autoComplete="off"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setError(null);
              }}
              onBlur={() => setUsernameEdited(true)}
              style={{
                borderRadius: '60px',
                border: '3px solid #e3e3e3',
                textAlign: 'center',
              }}
              className="neonInput"
            />
          </Form.Field>
        </Form>

        {error && <Message negative>{error}</Message>}

        <RoundedButton
          loading={doneButtonLoading}
          disabled={!validLeaderboardUsername(username)}
          onClick={async () => {
            setDoneButtonLoading(true);

            const usernameChangeResult =
              await playerApiClient.auth.updateDetails({
                body: { player: { friendlyName: username } },
              });

            if (usernameChangeResult.status !== HttpStatusCode.OK) {
              setError(usernameChangeResult.body.message);
              setDoneButtonLoading(false);
              return;
            }

            if (selectedFile) {
              const photoUploadResult =
                await playerApiClient.auth.uploadAvatarIcon({
                  body: { image: selectedFile },
                });

              if (photoUploadResult.status !== HttpStatusCode.OK) {
                setError(photoUploadResult.body.message);
                setDoneButtonLoading(false);
                return;
              }

              displayToast({
                title: 'Successfully updated details.',
                timeout: 3500,
              });

              onSuccess();
            } else if (hadPictureBefore && clearedExistingPicture) {
              await playerApiClient.auth.updateDetails({
                body: { player: { avatarLocation: null } },
              });

              displayToast({
                title: 'Successfully updated details.',
                timeout: 3500,
              });

              onSuccess();
            } else {
              displayToast({
                title: 'Successfully updated details.',
                timeout: 3500,
              });

              onSuccess();
            }
          }}
          style={{
            marginTop: '1em',
            width: '100%',
            height: '40px',
          }}
          href={undefined}
          className={undefined}
          id={undefined}
          hoverColor={undefined}
          hoverTextColor={undefined}
        >
          SAVE CHANGES
        </RoundedButton>

        <style jsx>{`
          :global(#message > *) {
            flex-shrink: 0;
          }

          #closeButton {
            position: fixed;
            width: 20px;
            height: 20px;
            right: 16px;
            top: 16px;
          }

          .randomIcon {
            width: 20px;
            height: auto;
            margin-bottom: 5px;
          }

          .initialsCircle {
            width: 120px;
            height: 120px;
            background-color: ${colors.primaryBrandGray};
            border-radius: 60px;
            box-shadow: 0 0 15px ${colors.brand.neon};
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 4em;
            font-weight: bold;
          }
        `}</style>
      </PopupWithDimmedBackground>
    );
  }
);

export default LeaderboardDetailsChooser;
